// Build a structured route/entity context from React Router state and the
// current user. Consumed by AiCopilotDrawer and AiAskPage so the API call
// to POST /ai/ask carries page context the mock provider can use to ground
// answers in what the user is actually looking at.

const ROUTE_NAMES = {
  'gate-review': 'Gate Review',
  project: 'Project',
  portfolio: 'Portfolio',
  program: 'Program',
  executive: 'Executive Command Center',
  delivery: 'Delivery Workspace',
  roadmap: 'Strategic Roadmap',
  financials: 'Financial Lifecycle',
  lifecycle: 'Portfolio Lifecycle',
  'decision-support': 'Decision Support',
  knowledge: 'Knowledge Library',
  resources: 'Resource Command Center',
  rapid: 'RAPID',
  'mission-impact': 'Mission Impact Analytics',
  'ai-orchestration': 'AI Orchestration',
  'ai-ask': 'Ask PRIME',
  lineage: 'Lineage',
  connectors: 'Connectors',
  rbac: 'RBAC Admin',
  audit: 'Audit Log',
  notifications: 'Notifications',
  exports: 'Briefings & Exports',
  home: 'Home',
  'market-research': 'Market Research',
  'saved-views': 'Saved Views',
  profile: 'My Profile',
  settings: 'Settings',
};

// Ordered most-specific first.
const MATCHERS = [
  {
    re: /^\/projects\/([^/]+)\/gate-review\/([^/]+)/,
    route: 'gate-review',
    fields: ['projectId', 'gateId'],
    entityType: 'gate',
    entityIdFrom: 'gateId',
  },
  {
    re: /^\/rapids\/([^/]+)/,
    route: 'rapid',
    fields: ['rapidId'],
    entityType: 'rapid',
    entityIdFrom: 'rapidId',
  },
  {
    re: /^\/projects\/([^/]+)/,
    route: 'project',
    fields: ['projectId'],
    entityType: 'project',
    entityIdFrom: 'projectId',
  },
  { re: /^\/rapids/, route: 'rapid', entityType: 'rapid', entityId: null },
  {
    re: /^\/portfolios\/([^/]+)/,
    route: 'portfolio',
    fields: ['portfolioId'],
    entityType: 'portfolio',
    entityIdFrom: 'portfolioId',
  },
  {
    re: /^\/programs\/([^/]+)/,
    route: 'program',
    fields: ['programId'],
    entityType: 'program',
    entityIdFrom: 'programId',
  },
  { re: /^\/executive/, route: 'executive', entityType: 'portfolio' },
  { re: /^\/delivery/, route: 'delivery', entityType: 'workspace' },
  { re: /^\/roadmap/, route: 'roadmap', entityType: 'roadmap' },
  { re: /^\/financials/, route: 'financials', entityType: 'financials' },
  { re: /^\/lifecycle/, route: 'lifecycle', entityType: 'lifecycle' },
  { re: /^\/decision-support/, route: 'decision-support', entityType: 'decision' },
  {
    re: /^\/knowledge\/artifacts\/([^/]+)/,
    route: 'knowledge',
    fields: ['artifactId'],
    entityType: 'artifact',
    entityIdFrom: 'artifactId',
  },
  { re: /^\/knowledge/, route: 'knowledge', entityType: 'knowledge' },
  { re: /^\/resources\/assignments\/new/, route: 'resources', entityType: 'assignment-request' },
  { re: /^\/resources/, route: 'resources', entityType: 'resource' },
  { re: /^\/analytics\/resources/, route: 'resources', entityType: 'resource' },
  { re: /^\/analytics\/mission-impact/, route: 'mission-impact', entityType: 'mission-impact' },
  { re: /^\/ai\/orchestration/, route: 'ai-orchestration', entityType: 'ai-orchestration' },
  { re: /^\/ai\/ask/, route: 'ai-ask', entityType: 'ai-ask' },
  { re: /^\/admin\/lineage/, route: 'lineage', entityType: 'lineage' },
  { re: /^\/admin\/data/, route: 'connectors', entityType: 'connectors' },
  { re: /^\/admin\/rbac/, route: 'rbac', entityType: 'rbac' },
  { re: /^\/admin\/audit/, route: 'audit', entityType: 'audit' },
  { re: /^\/notifications/, route: 'notifications', entityType: 'notifications' },
  { re: /^\/exports/, route: 'exports', entityType: 'exports' },
  { re: /^\/market-research/, route: 'market-research', entityType: 'market-research' },
  { re: /^\/saved-views/, route: 'saved-views', entityType: 'saved-views' },
  { re: /^\/me\/profile/, route: 'profile', entityType: 'profile' },
  { re: /^\/settings/, route: 'settings', entityType: 'settings' },
  { re: /^\/home/, route: 'home', entityType: 'home' },
];

export function buildRouteContext({ location, currentUser } = {}) {
  const pathname = location?.pathname || '/';
  const search = location?.search || '';
  const personaUserId = currentUser?.user?.id || null;
  // currentUser.roles is an array of role-id strings (per CurrentUserContext +
  // TopBar usage), not role objects. Stay tolerant in case the shape changes.
  const personaRoles = (currentUser?.roles || [])
    .map((r) => (typeof r === 'string' ? r : r?.id))
    .filter(Boolean);

  for (const m of MATCHERS) {
    const match = pathname.match(m.re);
    if (match) {
      const ctx = {
        pathname,
        search,
        route: m.route,
        entityType: m.entityType,
        personaUserId,
        personaRoles,
      };
      if (m.fields) {
        m.fields.forEach((f, i) => {
          ctx[f] = match[i + 1];
        });
      }
      if (m.entityIdFrom) {
        ctx.entityId = ctx[m.entityIdFrom];
      } else if (Object.prototype.hasOwnProperty.call(m, 'entityId')) {
        ctx.entityId = m.entityId;
      }
      return ctx;
    }
  }

  return {
    pathname,
    search,
    route: 'unknown',
    entityType: null,
    personaUserId,
    personaRoles,
  };
}

export function routeContextLabel(ctx) {
  if (!ctx) return '';
  const routeName = ROUTE_NAMES[ctx.route] || ctx.route;
  return routeName;
}

export function entityIdChip(ctx) {
  if (!ctx) return null;
  return ctx.entityId || null;
}
